import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Send, Download, Plus, X, ArrowLeft, Eye } from "lucide-react";

const STEPS = [["observation", "Observation"], ["clarify", "Clarify"], ["draft", "Draft"],
  ["debrief", "Debrief and revise"], ["export", "Export"]];
const SPEAKERS = ["Unknown", "Coach", "Teacher", "Other"];

export default function ReflectionWork() {
  const { reflectionId } = useParams();
  const [r, setR] = useState(null);
  const [step, setStep] = useState("observation");
  const [busy, setBusy] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const [recipients, setRecipients] = useState([]);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const pending = useRef({});
  const timer = useRef(null);
  const flushing = useRef(false);

  const load = useCallback(() => api.get(`/reflections/${reflectionId}`).then((x) => setR(x.data)).catch((e) => toast.error(apiError(e))), [reflectionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (step === "export") api.get(`/reflections/${reflectionId}/share/recipients`).then((x) => setRecipients(x.data)).catch(() => {});
  }, [step, reflectionId]);

  const flush = useCallback(async () => {
    if (flushing.current) return;
    const payload = pending.current;
    if (!Object.keys(payload).length) return;
    pending.current = {};
    flushing.current = true;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/reflections/${reflectionId}`, payload);
      setR(data);
      setSaveState("saved");
    } catch (e) {
      pending.current = { ...payload, ...pending.current };
      setSaveState("failed");
      toast.error("Save failed — your text is preserved on this page. Use Retry.");
    } finally { flushing.current = false; }
  }, [reflectionId]);

  const edit = (fields) => {
    setR((cur) => ({ ...cur, ...fields }));
    pending.current = { ...pending.current, ...fields };
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  };

  const ai = async (name, label) => {
    await flush();
    setBusy(name);
    try {
      await api.post(`/reflections/${reflectionId}/ai/${name}`);
      await load();
      if (name === "draft") setStep("draft");
      toast.success(label);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const candidateAct = async (action) => {
    setBusy("candidate");
    try { await api.post(`/reflections/${reflectionId}/candidate/${action}`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const approveShare = async () => {
    try { await api.post(`/reflections/${reflectionId}/share/approve`); await load(); toast.success("Feedback approved to share — separate from billing approval."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post(`/reflections/${reflectionId}/send`, { to, cc });
      const failed = (data.results || []).filter((x) => x.status !== "sent");
      if (!failed.length) toast.success(`Sent to ${data.results.length} recipient(s) — accepted by the email provider.`);
      else toast.error(`${data.results.length - failed.length} sent, ${failed.length} failed.`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!r) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="reflection-loading">Loading teacher summary…</div>;

  const ctx = r.context || {};
  const notes = r.notes || {};
  const clarify = r.clarify || { questions: [] };
  const fb = r.feedback || {};
  const cur = fb.current;
  const cand = r.candidate;
  const debrief = r.debrief || {};
  const share = r.share || {};
  const shareReady = share.approved_hash && share.approved_hash === cur?._hash;
  const setCtx = (k, val) => edit({ context: { ...ctx, [k]: val } });
  const setNotes = (fields) => edit({ notes: { ...notes, ...fields } });
  const setDebrief = (fields) => edit({ debrief: { ...debrief, ...fields } });
  const setQ = (i, fields) => edit({ clarify: { ...clarify, questions: clarify.questions.map((q, j) => (j === i ? { ...q, ...fields } : q)) } });
  const setFb = (fields) => edit({ feedback: { ...fb, ...fields } });
  const setContent = (fields) => setFb({ current: { ...cur, ...fields } });
  const setItem = (key, i, text) => setContent({ [key]: cur[key].map((x, j) => (j === i ? { ...x, text } : x)) });
  const addItem = (key, blank) => setContent({ [key]: [...(cur[key] || []), blank] });
  const delItem = (key, i) => setContent({ [key]: cur[key].filter((_, j) => j !== i) });
  const setFormRow = (sec, i, fields) => setNotes({ form: { ...notes.form, [sec]: notes.form[sec].map((row, j) => (j === i ? { ...row, ...fields } : row)) } });
  const words = cur ? [cur.summary, ...(cur.glows || []).map((g) => g.text), ...(cur.grows || []).map((g) => g.text),
    ...(cur.nextSteps || []).map((n) => n.text), (cur.followUp || {}).text].join(" ").split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="max-w-6xl space-y-5" data-testid="reflection-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/visits/${r.visit_id}`} className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1" data-testid="back-to-visit"><ArrowLeft size={14} /> Visit log</Link>
          <h1 className="font-serif font-bold text-xl text-navy" data-testid="reflection-title">Teacher summary — {ctx.teacher || "…"}</h1>
        </div>
        <span className={`text-xs font-bold ${saveState === "failed" ? "text-[#991B1B]" : "text-[#78716C]"}`} data-testid="refl-save-state">
          {saveState === "saving" ? "Saving…" : saveState === "failed" ? "Not saved — text preserved" : saveState === "dirty" ? "Unsaved changes…" : "All changes saved"}
          {saveState === "failed" && <button className="btn-ghost !py-0.5 !px-2 text-xs ml-2" onClick={flush} data-testid="refl-save-retry">Retry</button>}
        </span>
      </div>
      <div className="rounded-lg border border-[#E7E1D4] bg-sand/60 px-4 py-2.5 text-xs text-[#57534E]" data-testid="reflection-privacy-note">
        Saved to the visit automatically. Notes and analysis are visible to you and CKingEducation administrators only; the teacher receives only the reviewed feedback you send. It never appears on invoices or in the principal summary.
      </div>

      <ol className="flex gap-1 border-b border-[#E7E1D4] overflow-x-auto" data-testid="reflection-steps">
        {STEPS.map(([k, l], i) => (
          <li key={k}><button onClick={() => setStep(k)} data-testid={`step-${k}`}
            className={`px-4 py-2 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${step === k ? "border-terracotta text-navy" : "border-transparent text-[#78716C] hover:text-navy"}`}>
            {i + 1}. {l}</button></li>
        ))}
      </ol>

      {step === "observation" && (
        <div className="space-y-4" data-testid="observation-step">
          <div className="card p-6 grid sm:grid-cols-3 gap-4">
            <div><label className="label">Teacher (name or alias)</label><input className="input" value={ctx.teacher} data-testid="ctx-teacher" onChange={(e) => setCtx("teacher", e.target.value)} /></div>
            <div><label className="label">Observation date</label><input className="input" value={ctx.date} data-testid="ctx-date" onChange={(e) => setCtx("date", e.target.value)} /></div>
            <div><label className="label">Grade(s)</label><input className="input" value={ctx.grades} data-testid="ctx-grades" onChange={(e) => setCtx("grades", e.target.value)} /></div>
            <div><label className="label">Lesson topic</label><input className="input" value={ctx.topic} data-testid="ctx-topic" onChange={(e) => setCtx("topic", e.target.value)} /></div>
            <div><label className="label">Stated learning objective</label><input className="input" value={ctx.objective} data-testid="ctx-objective" onChange={(e) => setCtx("objective", e.target.value)} /></div>
            <div><label className="label">Classroom setting</label><input className="input" value={ctx.setting} data-testid="ctx-setting" onChange={(e) => setCtx("setting", e.target.value)} /></div>
            <div><label className="label">First observation of this teacher?</label>
              <select className="input" value={ctx.firstObs} data-testid="ctx-firstobs" onChange={(e) => setCtx("firstObs", e.target.value)}>
                <option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></div>
            <div><label className="label">Coaching goal agreed beforehand?</label>
              <select className="input" value={ctx.goalAgreed} data-testid="ctx-goalagreed" onChange={(e) => setCtx("goalAgreed", e.target.value)}>
                <option value="">—</option><option value="yes">Yes</option><option value="no">No</option><option value="unknown">Unknown</option></select></div>
            {ctx.goalAgreed === "yes" && <div><label className="label">Agreed coaching goal</label><input className="input" value={ctx.goal} data-testid="ctx-goal" onChange={(e) => setCtx("goal", e.target.value)} /></div>}
          </div>

          <div className="card p-6 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-serif font-bold text-navy">Observation notes</h3>
              <div className="flex gap-1" data-testid="notes-mode">
                {[["paste", "Paste"], ["form", "Structured form"]].map(([k, l]) => (
                  <button key={k} className={`rounded-full px-3 py-1 text-xs font-bold border ${notes.mode === k ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`}
                    onClick={() => setNotes({ mode: k })} data-testid={`notes-mode-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            {notes.mode === "paste" ? (
              <>
                <textarea className="input min-h-[220px] font-mono text-sm" value={notes.corrected} placeholder="Paste observation notes…" data-testid="notes-text"
                  onChange={(e) => setNotes({ corrected: e.target.value })} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!notes.confirmed} data-testid="notes-reviewed"
                    onChange={(e) => setNotes({ confirmed: e.target.checked })} />
                  I've reviewed these notes (corrections here supersede anything unclear)</label>
              </>
            ) : (
              <div className="space-y-4">
                {[["launch", "Launch"], ["working", "Students working and thinking"], ["moves", "Teacher moves and student responses"], ["discussion", "Discussion and closing"]].map(([sec, label]) => (
                  <div key={sec} className="space-y-2" data-testid={`form-section-${sec}`}>
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-black uppercase tracking-wide text-[#78716C]">{label}</h4>
                      <button className="btn-ghost !py-0.5 text-xs" data-testid={`form-add-${sec}`}
                        onClick={() => setNotes({ form: { ...notes.form, [sec]: [...(notes.form[sec] || []), { time: "", teacher: "", student: "", coach: "" }] } })}><Plus size={12} /> Row</button>
                    </div>
                    {(notes.form[sec] || []).map((row, i) => (
                      <div key={i} className="grid sm:grid-cols-4 gap-2">
                        <input className="input !py-1.5 text-xs" placeholder="Time/context" value={row.time} data-testid={`form-${sec}-time-${i}`} onChange={(e) => setFormRow(sec, i, { time: e.target.value })} />
                        <input className="input !py-1.5 text-xs" placeholder="Teacher action/words" value={row.teacher} onChange={(e) => setFormRow(sec, i, { teacher: e.target.value })} />
                        <input className="input !py-1.5 text-xs" placeholder="Student action/words" value={row.student} onChange={(e) => setFormRow(sec, i, { student: e.target.value })} />
                        <input className="input !py-1.5 text-xs" placeholder="Coach question/interpretation" value={row.coach} onChange={(e) => setFormRow(sec, i, { coach: e.target.value })} />
                      </div>
                    ))}
                  </div>
                ))}
                <div><label className="label">Coach's questions and initial interpretations (hypotheses, not evidence)</label>
                  <textarea className="input min-h-[60px]" value={notes.form.coachQuestions || ""} data-testid="form-coach-questions"
                    onChange={(e) => setNotes({ form: { ...notes.form, coachQuestions: e.target.value } })} /></div>
              </div>
            )}
          </div>

          <div className="card p-6 space-y-3" data-testid="aliases-card">
            <div className="flex items-center justify-between">
              <h3 className="font-serif font-bold text-navy">Student names → aliases</h3>
              <button className="btn-ghost text-xs" onClick={() => ai("find-names", "Student names found and aliased.")} disabled={busy === "find-names"} data-testid="find-names">
                <Sparkles size={13} /> {busy === "find-names" ? "Looking…" : "AI: find likely student names"}</button>
            </div>
            <p className="text-xs text-[#57534E]">Aliases replace student names before any AI step and in every export.</p>
            {(r.aliases || []).map((a, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`alias-row-${i}`}>
                <input className="input !py-1.5 text-sm" value={a.name} placeholder="Student name"
                  onChange={(e) => edit({ aliases: r.aliases.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <span className="text-xs text-[#78716C]">→</span>
                <input className="input !py-1.5 text-sm" value={a.alias} placeholder="Alias"
                  onChange={(e) => edit({ aliases: r.aliases.map((x, j) => (j === i ? { ...x, alias: e.target.value } : x)) })} />
                <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => edit({ aliases: r.aliases.filter((_, j) => j !== i) })}><X size={12} /></button>
              </div>
            ))}
            <button className="btn-ghost text-xs" onClick={() => edit({ aliases: [...(r.aliases || []), { name: "", alias: `Student ${String.fromCharCode(65 + (r.aliases || []).length)}` }] })} data-testid="add-alias"><Plus size={12} /> Add alias</button>
          </div>

          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => ai("clarify", "Clarifying questions ready.")} disabled={busy === "clarify" || !((notes.corrected || "").trim() || notes.mode === "form")} data-testid="run-clarify">
              <Sparkles size={15} /> {busy === "clarify" ? "Checking…" : "Continue: check what needs clarifying"}</button>
          </div>
        </div>
      )}

      {step === "clarify" && (
        <div className="space-y-4" data-testid="clarify-step">
          <div className="card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-serif font-bold text-navy">Clarifying questions</h3>
              <button className="btn-ghost text-xs" onClick={() => ai("clarify", "Checked again.")} disabled={busy === "clarify"} data-testid="clarify-again"><Sparkles size={13} /> Check again</button>
            </div>
            {clarify.sufficient && <p className="text-sm text-[#166534]" data-testid="clarify-sufficient">The notes look sufficient — nothing material is missing.</p>}
            {clarify.questions.map((q, i) => (
              <div key={q.id} className="rounded-lg border border-[#E7E1D4] p-3 space-y-2" data-testid={`clarify-q-${i}`}>
                <div className="text-sm font-bold text-navy">{q.question}</div>
                {q.why && <div className="text-xs text-[#78716C]">Why it matters: {q.why}</div>}
                <textarea className="input !py-1.5 text-sm" placeholder="Your answer…" value={q.answer} data-testid={`clarify-answer-${i}`}
                  onChange={(e) => setQ(i, { answer: e.target.value, status: e.target.value.trim() ? "answered" : "open" })} />
                <div className="flex gap-2 text-xs">
                  {[["unknown", "Unknown"], ["teacher", "Leave for the teacher"]].map(([k, l]) => (
                    <button key={k} className={`rounded-full px-3 py-1 font-bold border ${q.status === k ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`}
                      onClick={() => setQ(i, { status: q.status === k ? "open" : k })} data-testid={`clarify-${k}-${i}`}>{l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-serif font-bold text-navy">Corrections and added context</h3>
              <button className="btn-ghost text-xs" onClick={() => edit({ corrections: [...(r.corrections || []), { text: "", at: new Date().toISOString() }] })} data-testid="add-correction"><Plus size={12} /> Add</button>
            </div>
            <p className="text-xs text-[#57534E]">Corrections supersede the notes; the original notes remain the record of what was written.</p>
            {(r.corrections || []).map((c, i) => (
              <div key={i} className="flex gap-2" data-testid={`correction-${i}`}>
                <textarea className="input !py-1.5 text-sm flex-1" value={c.text}
                  onChange={(e) => edit({ corrections: r.corrections.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
                <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => edit({ corrections: r.corrections.filter((_, j) => j !== i) })}><X size={12} /></button>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => ai("draft", "Feedback drafted — review it below.")} disabled={busy === "draft"} data-testid="run-draft">
              <Sparkles size={15} /> {busy === "draft" ? "Drafting…" : "Draft feedback (analysis + guide + teacher feedback)"}</button>
          </div>
        </div>
      )}

      {step === "draft" && (
        <div className="grid lg:grid-cols-3 gap-5" data-testid="draft-step">
          <div className="lg:col-span-2 space-y-4">
            {cand && (
              <div className="rounded-lg border-2 border-[#FCD34D] bg-[#FFFBEB] p-4 space-y-2" data-testid="candidate-card">
                <div className="text-sm font-bold text-navy">{cand.label} <span className="badge-draft ml-2">Not applied</span></div>
                {(cand.changes || []).map((c, i) => <p key={i} className="text-xs text-[#57534E]">• {c}</p>)}
                <div className="text-xs text-[#78716C]">Summary: {(cand.content || {}).summary}</div>
                <div className="flex gap-2">
                  <button className="btn-primary !py-1.5 text-xs" onClick={() => candidateAct("apply")} disabled={busy === "candidate"} data-testid="candidate-apply">Apply this version</button>
                  <button className="btn-ghost !py-1.5 text-xs" onClick={() => candidateAct("discard")} data-testid="candidate-discard">Discard</button>
                </div>
              </div>
            )}
            {!cur ? (
              <div className="card p-6 text-sm text-[#78716C]" data-testid="draft-empty">
                No feedback yet. Go back to Clarify and run <strong>Draft feedback</strong>.</div>
            ) : (
              <div className="card p-6 space-y-4" data-testid="feedback-editor">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-serif font-bold text-navy">Teacher-facing feedback</h3>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">{[["brief", "Brief"], ["detailed", "Detailed"]].map(([k, l]) => (
                      <button key={k} className={`rounded-full px-3 py-1 text-xs font-bold border ${fb.mode === k ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`}
                        onClick={() => setFb({ mode: k })} data-testid={`mode-${k}`}>{l}</button>))}</div>
                    <span className={`text-xs font-bold ${words > (fb.mode === "detailed" ? 700 : 350) ? "text-[#991B1B]" : "text-[#78716C]"}`} data-testid="word-count">{words} words</span>
                    <button className="btn-ghost !py-1 text-xs" onClick={() => ai("shorten", "Shorter version ready for review.")} disabled={busy === "shorten"} data-testid="shorten-button"><Sparkles size={12} /> Shorten</button>
                  </div>
                </div>
                <div><label className="label">Summary</label>
                  <textarea className="input min-h-[70px]" value={cur.summary} data-testid="fb-summary" onChange={(e) => setContent({ summary: e.target.value })} /></div>
                {[["glows", "Glows"], ["grows", "Grows"]].map(([key, label]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between"><label className="label !mb-1">{label}</label>
                      <button className="btn-ghost !py-0.5 text-xs" onClick={() => addItem(key, { text: "", ev: [] })} data-testid={`add-${key}`}><Plus size={12} /></button></div>
                    {(cur[key] || []).map((g, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <textarea className="input !py-1.5 text-sm flex-1" value={g.text} data-testid={`fb-${key}-${i}`} onChange={(e) => setItem(key, i, e.target.value)} />
                        <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => delItem(key, i)}><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                ))}
                <div>
                  <div className="flex items-center justify-between"><label className="label !mb-1">Next steps</label>
                    <button className="btn-ghost !py-0.5 text-xs" onClick={() => addItem("nextSteps", { text: "", status: "Proposed", ev: [] })} data-testid="add-nextstep"><Plus size={12} /></button></div>
                  {(cur.nextSteps || []).map((n, i) => (
                    <div key={i} className="flex gap-2 mb-2 items-center">
                      <textarea className="input !py-1.5 text-sm flex-1" value={n.text} data-testid={`fb-nextstep-${i}`} onChange={(e) => setItem("nextSteps", i, e.target.value)} />
                      <button className={`rounded-full px-2.5 py-1 text-xs font-bold border ${n.status === "Agreed" ? "bg-[#166534] text-white border-[#166534]" : "bg-white text-navy border-[#E7E1D4]"}`}
                        onClick={() => setContent({ nextSteps: cur.nextSteps.map((x, j) => (j === i ? { ...x, status: x.status === "Agreed" ? "Proposed" : "Agreed" } : x)) })}
                        data-testid={`fb-nextstep-status-${i}`}>{n.status}</button>
                      <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => delItem("nextSteps", i)}><X size={12} /></button>
                    </div>
                  ))}
                </div>
                <div><label className="label">Follow-up (one question or evidence focus)</label>
                  <input className="input" value={(cur.followUp || {}).text || ""} data-testid="fb-followup"
                    onChange={(e) => setContent({ followUp: { ...(cur.followUp || {}), text: e.target.value } })} /></div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E7E1D4]">
                  <button className="btn-outline !py-1.5 text-xs" onClick={() => ai("draft", "New draft ready for review.")} disabled={busy === "draft"} data-testid="redraft"><Sparkles size={12} /> New AI draft</button>
                  <button className="btn-primary !py-1.5 text-xs" onClick={approveShare} data-testid="approve-share"><CheckCircle2 size={13} /> Approve to share with teacher</button>
                  {share.approved_hash && <span className="badge-approved" data-testid="share-approved-badge">Approved to share{share.approved_hash !== undefined ? "" : ""}</span>}
                </div>
              </div>
            )}
            {(fb.versions || []).length > 0 && (
              <details className="card p-4" data-testid="version-history">
                <summary className="text-xs font-bold text-navy cursor-pointer">Version history ({fb.versions.length})</summary>
                <ul className="mt-2 space-y-1">{fb.versions.map((ver) => (
                  <li key={ver.id} className="text-xs text-[#57534E]">{ver.label} · {new Date(ver.at).toLocaleString()}</li>))}</ul>
              </details>
            )}
          </div>
          <div className="space-y-4">
            {r.analysis && (
              <div className="card p-5 space-y-3 border-l-4 border-l-navy" data-testid="coach-panel">
                <h3 className="font-serif font-bold text-navy text-sm">Coach-only analysis</h3>
                {(r.analysis.focusAreas || []).map((f, i) => (
                  <div key={i} className="text-xs"><span className="font-bold text-terracotta">{f.area}</span><p className="text-[#57534E]">{f.rationale}</p></div>
                ))}
                {[["observed", "Observed facts"], ["interpretations", "Interpretations (tentative)"], ["teacherReported", "Teacher-reported"],
                  ["notObserved", "Not observed (≠ didn't happen)"], ["cautions", "Cautions"]].map(([k, l]) => (
                  (r.analysis[k] || []).length > 0 && (
                    <div key={k}><div className="text-xs font-black uppercase tracking-wide text-[#78716C]">{l}</div>
                      <ul className="text-xs text-[#57534E] list-disc ml-4">{r.analysis[k].map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                  )
                ))}
              </div>
            )}
            {(r.guide?.items || []).length > 0 && (
              <div className="card p-5 space-y-2" data-testid="guide-panel">
                <h3 className="font-serif font-bold text-navy text-sm">Conversation guide (coach-only)</h3>
                {r.guide.items.map((g, i) => (
                  <div key={i} className="text-xs" data-testid={`guide-${i}`}>
                    <div className="font-bold text-navy">{g.topic}</div>
                    <div className="text-[#57534E]">{g.question}</div>
                    <div className="text-[#78716C]">Listen for: {g.listenFor}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {step === "debrief" && (
        <div className="space-y-4" data-testid="debrief-step">
          <div className="card p-6 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-serif font-bold text-navy">Debrief material</h3>
              <div className="flex gap-1">{[["transcript", "Transcript"], ["summary", "Summary only"]].map(([k, l]) => (
                <button key={k} className={`rounded-full px-3 py-1 text-xs font-bold border ${debrief.mode === k ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`}
                  onClick={() => setDebrief({ mode: k })} data-testid={`debrief-mode-${k}`}>{l}</button>))}</div>
            </div>
            {debrief.mode === "transcript" ? (
              <>
                <div className="flex gap-2">
                  <button className="btn-ghost text-xs" data-testid="add-line"
                    onClick={() => setDebrief({ lines: [...(debrief.lines || []), { text: "", speaker: "Unknown" }] })}><Plus size={12} /> Add line</button>
                  <button className="btn-outline text-xs" onClick={() => ai("attribution", "Speaker labels proposed — review them.")} disabled={busy === "attribution" || !(debrief.lines || []).length} data-testid="run-attribution">
                    <Sparkles size={12} /> {busy === "attribution" ? "Proposing…" : "AI: propose speakers"}</button>
                </div>
                {(debrief.lines || []).map((l, i) => (
                  <div key={i} className="flex gap-2 items-start" data-testid={`debrief-line-${i}`}>
                    <select className={`input !py-1 text-xs !w-28 ${l.flag ? "border-[#FCA5A5]" : ""}`} value={l.speaker} data-testid={`debrief-speaker-${i}`}
                      onChange={(e) => setDebrief({ lines: debrief.lines.map((x, j) => (j === i ? { ...x, speaker: e.target.value, speakerSet: true, flag: false } : x)) })}>
                      {SPEAKERS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <textarea className="input !py-1.5 text-sm flex-1" value={l.text} data-testid={`debrief-text-${i}`}
                      onChange={(e) => setDebrief({ lines: debrief.lines.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
                    <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => setDebrief({ lines: debrief.lines.filter((_, j) => j !== i) })}><X size={12} /></button>
                  </div>
                ))}
              </>
            ) : (
              <textarea className="input min-h-[120px]" placeholder="Write a short debrief summary…" value={debrief.summaryText || ""} data-testid="debrief-summary-text"
                onChange={(e) => setDebrief({ summaryText: e.target.value })} />
            )}
            <button className="btn-outline" onClick={() => ai("debrief-summary", "Debrief organized — confirm what was actually agreed.")} disabled={busy === "debrief-summary"} data-testid="run-debrief-summary">
              <Sparkles size={14} /> {busy === "debrief-summary" ? "Organizing…" : "Organize the debrief"}</button>
          </div>

          {debrief.summary && (
            <div className="card p-6 space-y-3" data-testid="debrief-summary">
              <h3 className="font-serif font-bold text-navy">Debrief summary <span className="text-xs font-normal text-[#78716C]">— confirm explicit agreements; acknowledgments are not commitments</span></h3>
              {[["teacherPerspective", "Teacher perspective (teacher-reported)"], ["coachExplanations", "Coach explanations (your words)"],
                ["suggestionsNotAgreed", "Suggestions NOT agreed (stay Proposed)"], ["unresolved", "Unresolved"]].map(([k, l]) => (
                (debrief.summary[k] || []).length > 0 && (
                  <div key={k}><div className="text-xs font-black uppercase tracking-wide text-[#78716C]">{l}</div>
                    <ul className="text-sm space-y-1">{debrief.summary[k].map((x, i) => <li key={x.id || i}>• {x.text}{x.quote ? <span className="text-xs text-[#78716C]"> — “{x.quote}”</span> : ""}</li>)}</ul></div>
                )
              ))}
              <div><div className="text-xs font-black uppercase tracking-wide text-[#78716C]">Agreed actions — tick only what was EXPLICITLY agreed</div>
                {(debrief.summary.agreedActions || []).map((a, i) => (
                  <label key={a.id || i} className="flex items-center gap-2 text-sm" data-testid={`agreed-${i}`}>
                    <input type="checkbox" checked={!!a.confirmed} data-testid={`agreed-check-${i}`}
                      onChange={(e) => setDebrief({ summary: { ...debrief.summary, agreedActions: debrief.summary.agreedActions.map((x, j) => (j === i ? { ...x, confirmed: e.target.checked } : x)) } })} />
                    {a.text} <span className="text-xs text-[#78716C]">({a.strength})</span>
                  </label>
                ))}
              </div>
              <button className="btn-primary" onClick={() => ai("revise", "Revision ready for review on the Draft step.")} disabled={busy === "revise" || !cur} data-testid="run-revise">
                <Sparkles size={14} /> {busy === "revise" ? "Revising…" : "Revise the feedback with this debrief"}</button>
              {!cur && <p className="text-xs text-[#991B1B]">Draft the feedback first (step 3).</p>}
            </div>
          )}
        </div>
      )}

      {step === "export" && (
        <div className="space-y-4" data-testid="export-step">
          {!cur ? (
            <div className="card p-6 text-sm text-[#78716C]">Nothing to export yet — draft the feedback first.</div>
          ) : (
            <>
              <div className="card p-6 space-y-3" data-testid="export-preview-card">
                <h3 className="font-serif font-bold text-navy flex items-center gap-2"><Eye size={15} /> Teacher-facing preview</h3>
                <p className="text-xs text-[#57534E]">Only the reviewed feedback appears — never your notes, analysis, or interpretations.</p>
                <div className="rounded-lg border border-[#E7E1D4] p-5 bg-white space-y-3" data-testid="export-preview">
                  <p className="text-sm">{cur.summary}</p>
                  {(cur.glows || []).length > 0 && <div><div className="text-xs font-black uppercase tracking-wide text-terracotta">What worked well</div>
                    <ul className="text-sm list-disc ml-5">{cur.glows.map((g, i) => <li key={i}>{g.text}</li>)}</ul></div>}
                  {(cur.grows || []).length > 0 && <div><div className="text-xs font-black uppercase tracking-wide text-terracotta">An opportunity to grow</div>
                    <ul className="text-sm list-disc ml-5">{cur.grows.map((g, i) => <li key={i}>{g.text}</li>)}</ul></div>}
                  {(cur.nextSteps || []).length > 0 && <div><div className="text-xs font-black uppercase tracking-wide text-terracotta">Next steps</div>
                    <ul className="text-sm list-disc ml-5">{cur.nextSteps.map((n, i) => <li key={i}>{n.text}{n.status === "Agreed" ? " (Agreed)" : ""}</li>)}</ul></div>}
                  {(cur.followUp || {}).text && <div><div className="text-xs font-black uppercase tracking-wide text-terracotta">To follow up</div><p className="text-sm">{cur.followUp.text}</p></div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <a className="btn-outline !py-1.5 text-xs" href={`${process.env.REACT_APP_BACKEND_URL}/api/reflections/${r.id}/export`} target="_blank" rel="noreferrer" data-testid="export-html"><Eye size={12} /> Print-ready page</a>
                  <a className="btn-outline !py-1.5 text-xs" href={`${process.env.REACT_APP_BACKEND_URL}/api/reflections/${r.id}/export.pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf"><Download size={12} /> Download PDF</a>
                  <button className="btn-ghost !py-1.5 text-xs" data-testid="copy-text"
                    onClick={() => { navigator.clipboard.writeText([cur.summary, ...(cur.glows || []).map((g) => "• " + g.text), ...(cur.grows || []).map((g) => "• " + g.text), ...(cur.nextSteps || []).map((n) => "• " + n.text)].join("\n")); toast.success("Copied."); }}>Copy text</button>
                </div>
              </div>
              <div className="card p-6 space-y-3" data-testid="send-card">
                <h3 className="font-serif font-bold text-navy flex items-center gap-2"><Send size={15} /> Send to the teacher</h3>
                {!share.approved_hash && <p className="text-xs text-[#991B1B]" data-testid="send-blocked-note">Approve the feedback to share first (Draft step) — sharing approval is separate from billing approval.</p>}
                <div className="flex flex-wrap gap-2" data-testid="send-to-list">
                  {recipients.map((x) => (
                    <label key={x.email} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer font-bold ${to.includes(x.email) ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`} data-testid={`send-to-${x.email}`}>
                      <input type="checkbox" className="hidden" checked={to.includes(x.email)}
                        onChange={(e) => setTo(e.target.checked ? [...to, x.email] : to.filter((y) => y !== x.email))} />
                      {x.name || x.email}
                    </label>
                  ))}
                </div>
                <button className="btn-primary" onClick={send} disabled={busy === "send" || !to.length || !share.approved_hash} data-testid="send-feedback">
                  <Send size={14} /> {busy === "send" ? "Sending…" : "Send feedback"}</button>
                {(share.sent_to || []).length > 0 && (
                  <div className="text-xs space-y-1" data-testid="sent-log">
                    {share.sent_to.map((s, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <span className={s.status === "sent" ? "badge-approved" : "badge-nosend"}>{s.status}</span>
                        <span className="text-[#57534E]">{s.email}</span>
                        {s.detail && <span className="text-[#78716C]">{s.detail}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
